import React, { useEffect, useMemo, useState } from "react";
import { Badge, Col, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";

import TableContainer from "../../../../../Components/Common/TableContainer";
import LoadingSpinner from "../../../../../Components/Common/Spinner";
import AppModal from "../../../../../Components/Common/AppModal";
import DeleteModal from "../../../../../Components/Common/DeleteModal";
import {
  createProductCategoryApiCall,
  deleteProductCategoryApiCall,
  getProductCategoryListApiCall,
  updateProductCategoryApiCall,
} from "../../../../../helpers/fakebackend_helper";

/**
 * Product Categories (Task 24745).
 *
 * "We have loads of products. How can we group these into categories that are
 * nested" - this is where the tree is built.
 *
 * Shown as a FLAT TABLE WITH INDENTATION rather than an expand/collapse tree.
 * The backend returns every row with its depth and full path already worked
 * out, and a flat table keeps the things that matter on a catalogue of this
 * size - search across every level at once, a product count per row, and a
 * stable place for the row menu. An expanding tree hides exactly the rows you
 * are hunting for.
 */

const emptyForm = {
  Id: null,
  Name: "",
  Code: "",
  Description: "",
  Parent_Id: "",
  Order: "",
  FlagActive: 1,
};

export default function ProductCategory() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await getProductCategoryListApiCall();
      setCategories(response?.productcategory_list || []);
    } catch (e) {
      toast.error("Could not load product categories");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // Search matches the whole PATH, not just the name, so typing "hardware"
  // finds the laptops filed underneath it. On a nested list, matching only the
  // leaf name is the behaviour that makes people think the search is broken.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter((c) =>
      [c.ZPath, c.Name, c.Code]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [categories, search]);

  // Parent options for the form. A category can never be moved inside itself
  // or one of its own descendants, so those are removed from the list rather
  // than offered and then rejected by the server.
  const parentOptions = useMemo(() => {
    const blocked = new Set();
    if (form.Id) {
      blocked.add(String(form.Id));
      // Anything whose path runs through this category is a descendant.
      const self = categories.find((c) => String(c.Id) === String(form.Id));
      if (self?.ZPath) {
        categories.forEach((c) => {
          if (
            String(c.Id) !== String(form.Id) &&
            String(c.ZPath || "").startsWith(`${self.ZPath} > `)
          ) {
            blocked.add(String(c.Id));
          }
        });
      }
    }
    return [
      { value: "", label: "— No parent (top level) —" },
      ...categories
        .filter((c) => !blocked.has(String(c.Id)))
        .map((c) => ({ value: String(c.Id), label: c.ZPath || c.Name })),
    ];
  }, [categories, form.Id]);

  const openCreate = (parentId = "") => {
    setForm({ ...emptyForm, Parent_Id: parentId ? String(parentId) : "" });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      Id: row.Id,
      Name: row.Name || "",
      Code: row.Code || "",
      Description: row.Description || "",
      Parent_Id: row.Parent_Id ? String(row.Parent_Id) : "",
      Order: row.Order ?? "",
      FlagActive: Number(row.FlagActive) === 0 ? 0 : 1,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.Name.trim()) {
      toast.error("A category needs a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        Name: form.Name.trim(),
        Code: form.Code.trim(),
        Description: form.Description,
        Parent_Id: form.Parent_Id === "" ? null : form.Parent_Id,
        Order: form.Order === "" ? null : form.Order,
        FlagActive: form.FlagActive ? 1 : 0,
      };
      const response = form.Id
        ? await updateProductCategoryApiCall([{ ...payload, Id: form.Id }])
        : await createProductCategoryApiCall([payload]);

      if (response?.status === 200) {
        toast.success(response.message);
        setModalOpen(false);
        load();
      } else {
        // The server refuses a cycle, a too-deep move and a delete that would
        // orphan something, and says why in plain words - surface that rather
        // than a generic failure.
        toast.error(response?.message || "Could not save the category");
      }
    } catch (e) {
      toast.error("Could not save the category");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      const response = await deleteProductCategoryApiCall([deleteTarget.Id]);
      if (response?.status === 200) {
        toast.success(response.message);
        load();
      } else {
        toast.error(response?.message || "Could not delete the category");
      }
    } catch (e) {
      toast.error("Could not delete the category");
    }
    setDeleteTarget(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Category",
        accessor: "Name",
        filterable: false,
        Cell: (cell) => {
          const row = cell.row.original;
          const depth = Number(row.ZDepth) || 0;
          return (
            <div
              className="d-flex align-items-center"
              // Indentation is the only thing carrying the hierarchy in a flat
              // table, so it has to be visible without being silly at depth 5.
              style={{ paddingLeft: `${depth * 18}px` }}
            >
              {depth > 0 && (
                <i className="ri-corner-down-right-line text-muted me-1"></i>
              )}
              <span
                className={`fw-medium${
                  Number(row.FlagActive) === 0 ? " text-muted" : ""
                }`}
              >
                {row.Name}
              </span>
              {Number(row.FlagActive) === 0 && (
                <Badge color="light" className="text-muted ms-2">
                  Inactive
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        Header: "Full path",
        accessor: "ZPath",
        filterable: false,
        Cell: (cell) => (
          <span className="text-muted fs-13">{cell.row.original?.ZPath}</span>
        ),
      },
      {
        Header: "Code",
        accessor: "Code",
        filterable: false,
        Cell: (cell) => cell.row.original?.Code || "-",
      },
      {
        Header: "Products",
        accessor: "ZProductCount",
        filterable: false,
        Cell: (cell) => (
          <Badge color="light" className="text-body">
            {cell.row.original?.ZProductCount ?? 0}
          </Badge>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        filterable: false,
        Cell: (cell) => (
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-soft-secondary"
              title="Add a sub-category inside this one"
              onClick={() => openCreate(cell.row.original?.Id)}
            >
              <i className="ri-add-line"></i>
            </button>
            <button
              className="btn btn-sm btn-soft-primary"
              title="Edit"
              onClick={() => openEdit(cell.row.original)}
            >
              <i className="ri-edit-line"></i>
            </button>
            <button
              className="btn btn-sm btn-soft-danger"
              title="Delete"
              onClick={() => setDeleteTarget(cell.row.original)}
            >
              <i className="ri-delete-bin-2-line"></i>
            </button>
          </div>
        ),
      },
    ],
    // openCreate/openEdit close over setState only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="p-2 m-2">
      {deleteTarget && (
        <DeleteModal
          show={!!deleteTarget}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteTarget(null)}
        />
      )}

      {modalOpen && (
        <AppModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={form.Id ? "Edit category" : "New category"}
          icon="ri-price-tag-3-line"
          onSubmit={handleSave}
          submitLabel={form.Id ? "Save" : "Create"}
          submitDisabled={!form.Name.trim()}
          loading={saving}
          dialogProps={{ maxWidth: "sm", fullWidth: true }}
        >
          <div className="mb-3">
            <Label>
              Name <span className="text-danger">*</span>
            </Label>
            <Input
              value={form.Name}
              autoFocus
              onChange={(e) => setForm({ ...form, Name: e.target.value })}
              placeholder="e.g. Laptops"
            />
          </div>

          <div className="mb-3">
            <Label>Sits inside</Label>
            <Select
              options={parentOptions}
              value={
                parentOptions.find(
                  (o) => String(o.value) === String(form.Parent_Id)
                ) || parentOptions[0]
              }
              onChange={(o) => setForm({ ...form, Parent_Id: o?.value ?? "" })}
              classNamePrefix="select"
            />
            <small className="text-muted">
              A category cannot sit inside itself or one of its own
              sub-categories, so those are not offered here.
            </small>
          </div>

          <Row>
            <Col sm={6}>
              <div className="mb-3">
                <Label>Code</Label>
                <Input
                  value={form.Code}
                  onChange={(e) => setForm({ ...form, Code: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </Col>
            <Col sm={6}>
              <div className="mb-3">
                <Label>Order</Label>
                <Input
                  type="number"
                  value={form.Order}
                  onChange={(e) => setForm({ ...form, Order: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </Col>
          </Row>

          <div className="mb-3">
            <Label>Description</Label>
            <Input
              type="textarea"
              rows={2}
              value={form.Description}
              onChange={(e) =>
                setForm({ ...form, Description: e.target.value })
              }
            />
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="productCategoryActive"
              checked={!!form.FlagActive}
              onChange={(e) =>
                setForm({ ...form, FlagActive: e.target.checked ? 1 : 0 })
              }
            />
            <label className="form-check-label" htmlFor="productCategoryActive">
              Active
            </label>
          </div>
        </AppModal>
      )}

      <Row className="g-4 align-items-center mb-2">
        <Col sm={4}>
          <div className="search-box">
            <Input
              type="search"
              className="form-control search"
              placeholder={
                categories.length
                  ? `Search ${categories.length} categories...`
                  : "Search categories..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="ri-search-line search-icon"></i>
          </div>
        </Col>
        <div className="col-sm-auto ms-auto">
          <button className="btn btn-success" onClick={() => openCreate()}>
            <i className="ri-add-line align-bottom me-1"></i>
            New category
          </button>
        </div>
      </Row>

      {loading ? (
        <LoadingSpinner />
      ) : categories.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="ri-price-tag-3-line fs-1 d-block mb-2"></i>
          No categories yet. Create a top-level one, then use the{" "}
          <i className="ri-add-line"></i> on its row to nest others inside it.
        </div>
      ) : (
        <TableContainer
          columns={columns}
          data={filtered}
          isGlobalFilter={false}
          isAddUserList={false}
          customPageSize={25}
          className="custom-header-css"
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle"
          theadClass="text-muted"
          isLeadsFilter={false}
          // Already ordered by the backend (Order, then Name) with parents
          // ahead of children - re-sorting by Id here would scramble the
          // indentation into nonsense.
          initialSortBy={[]}
        />
      )}
    </div>
  );
}
